import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, CalendarIcon, ArrowRightIcon } from 'lucide-react';

// LET OP
// Hier stond eerder een blok "Bewezen bij B2B-bedrijven" met +280% meer
// aanvragen en −67% kosten per lead. Die cijfers waren niet onderbouwd.
// Onbewezen resultaatclaims vallen onder misleidende handelspraktijken,
// dus ze zijn eruit. Ook stond er dat een concurrent "niet meer kan
// instappen", terwijl marktexclusiviteit alleen bij Platinum geldt.

const benefits = [{
  title: 'Je weet wat werkt voordat je bouwt',
  desc: 'Je investeert pas in structuur nadat is aangetoond welke zoekopdrachten in jouw markt aanvragen opleveren.'
}, {
  title: 'Je budget kan omlaag naarmate het werkt',
  desc: 'Zodra de organische posities stijgen, wordt het advertentiebudget minder nodig. Je bouwt iets op in plaats van iets te huren.'
}, {
  title: 'Je krijgt alle data, ook bij vertrek',
  desc: 'Het rapport, de campagnegegevens en de content zijn jouw eigendom. Geen accounts op onze naam waar je niet bij kunt.'
}, {
  title: 'Vindbaar in zoekmachines en in AI',
  desc: 'De structuur wordt zo opgezet dat je ook geciteerd kunt worden in ChatGPT, Perplexity en de AI-antwoorden binnen Google.'
}, {
  title: 'Je ziet in de eerste maand al beweging',
  desc: 'Tijdens de meetfase zie je live welke zoekopdrachten aanslaan. Je hoeft geen negentig dagen in het duister te zitten.'
}, {
  title: 'Marktexclusiviteit is mogelijk',
  desc: 'Bij het Platinum-pakket werken we in jouw markt en regio niet ook voor je directe concurrent. Bij de andere pakketten geldt dat niet.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.23, 1, 0.32, 1]
    }
  }
};
export function HowItWorksForYou() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-20 md:py-24 bg-[#F8FAFC]" data-id="element-236" data-mp="2t6hqf2tey6q0">
      <div className="max-w-[1200px] mx-auto px-6" data-id="element-237" data-mp="2t680ff8dbzkb">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-start" data-id="element-238" data-mp="2y52rt5keiwg1">
          {/* Left */}
          <motion.div className="lg:sticky lg:top-32" initial={{
          opacity: 0,
          y: 20
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5
        }} data-id="element-239" data-mp="2jgg1k8t8kka1">
            <div className="section-label" data-id="element-240" data-mp="20ivf18dy1xsl">Voor jou</div>
            <h2 className="text-[clamp(2rem,3.6vw,2.8rem)] font-bold text-brand-dark leading-tight tracking-tight mb-5" data-id="element-241" data-mp="25gx6ohpfr5fo">
              Wat deze aanpak je oplevert
            </h2>
            <p className="text-brand-gray text-[1.05rem] leading-relaxed mb-8" data-id="element-242" data-mp="1lx7sxho6kj73">
              Elke beslissing in het traject is terug te voeren op wat er in
              jouw markt is gemeten. Niet op trends, niet op aannames, en niet
              op een zoekwoordentool.
            </p>

            {/* Kernpunt, zonder onbewezen cijfers */}
            <div className="rounded-2xl p-8 border border-[#E8EDF8] bg-white mb-6" data-id="element-243" data-mp="24t7muy0pxwtb">
              <div className="text-[3.25rem] font-bold text-brand-primary leading-none mb-2" data-id="element-244" data-mp="1zv61g6ftlleo">
                90
              </div>
              <div className="text-brand-dark font-bold text-lg mb-2" data-id="element-245" data-mp="2eiheydvkbfhf">
                dagen meten
              </div>
              <div className="text-brand-gray text-[0.95rem] leading-relaxed" data-id="element-246" data-mp="29lhk6z45xz5m">
                Daarna weet je per zoekopdracht wat een aanvraag kost. Pas dan
                begint het bouwen.
              </div>
            </div>

            <a href="#gesprek" className="inline-flex items-center justify-center gap-2.5 w-full bg-brand-primary text-white font-semibold px-7 py-4 rounded-lg hover:opacity-90 transition-opacity duration-200" data-id="element-247" data-mp="gw887upcx3t1">
              <CalendarIcon size={18} data-id="element-248" data-mp="qnckgmnneak6" />
              Plan een presentatie
              <ArrowRightIcon size={16} data-id="element-249" data-mp="vle3j7l2q9vn" />
            </a>
          </motion.div>

          {/* Right: benefits */}
          <motion.div className="space-y-3" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-250" data-mp="3u3rzuhqg972k">
            {benefits.map(b => <motion.div key={b.title} className="flex gap-4 p-6 rounded-2xl border border-[#E8EDF8] bg-white" variants={itemVariants} data-id="element-251" data-mp="7u8p8wus7png">
                <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center mt-0.5" style={{
              background: '#EFF6FF'
            }} data-id="element-252" data-mp="1jafdx0dgvv3k">
                  <CheckIcon size={16} className="text-brand-primary" strokeWidth={2.5} data-id="element-253" data-mp="33szswx6oumiv" />
                </div>
                <div data-id="element-254" data-mp="2u2y0xub7sjyo">
                  <div className="font-bold text-brand-dark mb-1.5 text-[1rem]" data-id="element-255" data-mp="2u3g4lc07i0gm">
                    {b.title}
                  </div>
                  <div className="text-brand-gray text-[0.92rem] leading-relaxed" data-id="element-256" data-mp="2p5enuxq96d8b">
                    {b.desc}
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}